import { useState, useEffect } from 'react';
import { Product, UseProductsResult, UseProductsParams } from '../types';
import { ProductService } from '../services/productService';

export function useProducts(params: UseProductsParams = {}): UseProductsResult {
  const { departmentId, categoryId, search, page = 1, sort, enabled = true, size = 15, userId } = params;
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(page);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setProducts([]);
      return;
    }

    let cancelled = false;

    const fetchProducts = async () => {
      // Yield to microtask queue so StrictMode cleanup can set cancelled=true before we start
      await Promise.resolve();
      if (cancelled) return;

      if (!window.__USER_FETCHED__) {
        await new Promise<void>((resolve) => {
          const interval = setInterval(() => {
            if (window.__USER_FETCHED__) {
              clearInterval(interval);
              resolve();
            }
          }, 50);
        });
      }

      if (cancelled) return;

      try {
        setLoading(true);
        setError(null);
        const response = await ProductService.getProducts(page, departmentId, categoryId, search, sort, size);

        if (!cancelled) {
          setProducts(response.data);
          setCurrentPage(response.currentPage);
          setTotalPages(response.totalPages);
          setTotal(response.total);
        }
      } catch (err) {
        if (!cancelled) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load products';
          setError(errorMessage);
          console.error('Error fetching products:', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProducts();

    return () => { cancelled = true; };
  }, [page, departmentId, categoryId, search, sort, enabled, size, userId]);

  return { products, loading, error, currentPage, totalPages, total };
}
